export class ClimateChartHelper {

    /**
     * Attach download handler to chart instance that adds title before download
     * @param chartInstance - The ECharts instance
     * @param chartTitle - Title to display in downloaded image
     * @param fileName - Name for downloaded file
     */
    public static attachDownloadHandler(chartInstance: any, chartTitle: string, fileName: string, firstColumnHeader: string = 'Period'): void {
        if (!chartInstance) return;
        
        // Store reference to chart title and filename
        const _chartTitle = chartTitle;
        const _fileName = fileName;
        
        // Get current toolbox config
        const currentOption = chartInstance.getOption();
        if (currentOption.toolbox && currentOption.toolbox[0]) {
            const toolbox = currentOption.toolbox[0];
            
            if (toolbox.feature) {
                // Hide default saveAsImage button
                if (toolbox.feature.saveAsImage) {
                    toolbox.feature.saveAsImage.show = false;
                }
                
                // Re-apply dataView optionToContent function (gets lost during option serialization)
                if (toolbox.feature.dataView) {
                    toolbox.feature.dataView.optionToContent = ClimateChartHelper.getDataViewTableFormatter(firstColumnHeader);
                }
                
                // Add custom download button
                toolbox.feature.myDownload = {
                    show: true,
                    title: 'Save as Image',
                    icon: 'path://M4.7,22.9L29.3,45.5L54.7,23.4M29.4,45.5L29.4,0',
                    onclick: function() {
                        console.log('Custom download button clicked!', _chartTitle, _fileName);
                        
                        // Store original options
                        const currentOpt = chartInstance.getOption();
                        const originalLegendTop = currentOpt.legend && currentOpt.legend[0] ? currentOpt.legend[0].top : undefined;
                        const originalGridTop = currentOpt.grid && currentOpt.grid[0] ? currentOpt.grid[0].top : undefined;
                        
                        // Wrap long titles manually (insert line break after ~60 characters at word boundary)
                        let wrappedTitle = _chartTitle;
                        if (_chartTitle.length > 60) {
                            const words = _chartTitle.split(' ');
                            let line = '';
                            const lines: string[] = [];
                            for (const word of words) {
                                if ((line + ' ' + word).trim().length > 60) {
                                    lines.push(line.trim());
                                    line = word;
                                } else {
                                    line = line ? line + ' ' + word : word;
                                }
                            }
                            if (line) lines.push(line.trim());
                            wrappedTitle = lines.join('\n');
                        }
                          // Get chart container and store original height
                        const chartDom = chartInstance.getDom();
                        const originalHeight = chartDom.style.height;
                        const originalHeightPx = chartDom.offsetHeight;
                        
                        // Increase container height temporarily for download
                        const extraHeight = wrappedTitle.includes('\n') ? 80 : 50;
                        chartDom.style.height = (originalHeightPx + extraHeight) + 'px';
                        chartInstance.resize();
                        
                        // Add title and adjust legend/grid to prevent overlap
                        chartInstance.setOption({
                            title: {
                                show: true,
                                text: wrappedTitle,
                                left: 'center',
                                top: 5,
                                textStyle: {
                                    fontSize: 11,
                                    fontWeight: 'normal',
                                    color: '#002d62'
                                }
                            },
                            legend: {
                                top: wrappedTitle.includes('\n') ? 35 : 25
                            },
                            grid: {
                                top: wrappedTitle.includes('\n') ? 80 : 65,
                                bottom: 50,
                                containLabel: true
                            }
                        }, false);
                        
                        // Wait for render, capture, and restore
                        setTimeout(() => {
                            const url = chartInstance.getDataURL({
                                type: 'png',
                                pixelRatio: 2,
                                backgroundColor: '#fff'
                            });
                            
                            // Remove title and restore original positions
                            chartInstance.setOption({
                                title: { show: false },
                                legend: {
                                    top: originalLegendTop !== undefined ? originalLegendTop : 10
                                },
                                grid: {
                                    top: originalGridTop !== undefined ? originalGridTop : 40
                                }
                            }, false);
                            
                            // Trigger download
                            const link = document.createElement('a');
                            link.download = _fileName + '.png';
                            link.href = url;
                            link.click();
                        }, 150);
                    }
                };
                
                // Update toolbox
                chartInstance.setOption({
                    toolbox: [toolbox]
                }, false);
            }
        }
    }
    
    /**
     * Get standardized toolbox configuration for climate charts
     * @param chartTitle - Title to display in the downloaded image header
     * @param fileName - Name for the downloaded file (without extension)
     * @param buttonColor - Color for the data view close button (default: #002d62 dark blue)
     * @returns ECharts toolbox configuration object
     */
    public static getToolboxConfig(chartTitle: string = '', fileName: string = 'chart', buttonColor: string = '#002d62'): any {
        return {
            show: true,
            orient: 'vertical',
            right: 2,
            top: 3,
            itemSize: 18,
            itemGap: 15,
            showTitle: true,
            backgroundColor: 'rgba(255, 255, 255, 1)',
            borderColor: '#002d62',
            borderWidth: 2,
            padding: 8,
            borderRadius: 4,
            iconStyle: {
                color: '#002d62',
                borderColor: '#002d62',
                borderWidth: 1.5
            },
            emphasis: {
                iconStyle: {
                    color: '#001f42',
                    borderColor: '#001f42',
                    borderWidth: 2,
                    shadowBlur: 4,
                    shadowColor: 'rgba(0, 45, 98, 0.3)'
                }
            },
            feature: {
                show: true,
                dataView: {
                    readOnly: true,
                    title: 'Data View',
                    lang: ['', 'Close'],
                    buttonColor: buttonColor,
                    buttonTextColor: '#fff',
                    icon: 'path://M17.5,17.3H33 M17.5,17.3H33 M45.4,29.5h-28 M11.5,2v56 M51.5,2v56 M51.5,29.5 M17.5,4.2h28 M17.5,42h28',
                    optionToContent: (opt: any) => {
                        const dataset = opt.dataset && opt.dataset[0] && opt.dataset[0].source ? opt.dataset[0].source : [];
                        if (!dataset.length) {
                            return '<div style="padding:8px;">No data available</div>';
                        }

                        const headerRow = dataset[0];
                        const rows = dataset.slice(1);

                        let html = '<div style="max-height:300px;overflow:auto;padding:8px;">';
                        html += '<table style="border-collapse:collapse;width:100%;font-size:12px;">';
                        html += '<thead><tr>';
                        html += '<th style="border:1px solid #ddd;padding:4px 8px;text-align:left;">Period</th>';
                        for (let i = 1; i < headerRow.length; i++) {
                            html += `<th style="border:1px solid #ddd;padding:4px 8px;text-align:center;">${headerRow[i]}</th>`;
                        }
                        html += '</tr></thead><tbody>';

                        rows.forEach((row: any[]) => {
                            html += '<tr>';
                            html += `<td style="border:1px solid #ddd;padding:4px 8px;white-space:nowrap;">${row[0]}</td>`;
                            // Iterate through all header columns to ensure proper alignment
                            for (let i = 1; i < headerRow.length; i++) {
                                if (i < row.length && row[i] !== undefined && row[i] !== null) {
                                    const value = typeof row[i] === 'number' ? row[i].toFixed(3) : (isNaN(Number(row[i])) ? row[i] : Number(row[i]).toFixed(3));
                                    html += `<td style="border:1px solid #ddd;padding:4px 8px;text-align:center;">${value}</td>`;
                                } else {
                                    // Fill missing cells with dash or empty
                                    html += `<td style="border:1px solid #ddd;padding:4px 8px;text-align:center;color:#999;">-</td>`;
                                }
                            }
                            html += '</tr>';
                        });

                        html += '</tbody></table></div>';
                        return html;
                    }
                },
                magicType: {
                    type: ['line', 'bar'],
                    title: {
                        line: 'Switch to Line Chart',
                        bar: 'Switch to Bar Chart'
                    },
                    icon: {
                        line: 'path://M4.1,28.9h7.1l9.3-22l7.4,38l9.7-19.7l3,12.8h14.9M4.1,58h51.4',
                        bar: 'path://M6.7,22.9h10V48h-10V22.9zM24.9,13h10v35h-10V13zM43.2,2h10v46h-10V2zM3.1,58h53.7'
                    }
                },
                saveAsImage: {
                    title: 'Save as Image',
                    name: fileName,
                    pixelRatio: 2
                }
            }
        };
    }

    public static calculateAxisBounds(seriesData: any, configMin: number | null, configMax: number | null, configInterval: number | null): {min: number, max: number, interval: number} {
        // 1) Scan series to find raw min/max and whether negatives exist
        let dataMin = Infinity;
        let dataMax = -Infinity;
        let hasNegativeValues = false;

        Object.values(seriesData).forEach(series => {
            if (!Array.isArray(series)) return;
            for (let i = 1; i < series.length; i++) {
                const numValue = parseFloat(series[i] as string);
                if (!isNaN(numValue)) {
                    dataMin = Math.min(dataMin, numValue);
                    dataMax = Math.max(dataMax, numValue);
                    if (numValue < 0) hasNegativeValues = true;
                }
            }
        });

        // Guard: if no data, default to [0, 1] with step 0.2
        if (!isFinite(dataMin) || !isFinite(dataMax)) {
            return { min: 0, max: 1, interval: 0.2 };
        }

        // 2) Establish starting min/max respecting non-negative rule
        let minValue = (!hasNegativeValues && dataMin >= 0) ? 0 : dataMin;
        let maxValue = dataMax;

        // If config bounds explicitly provided, apply them afterwards (with safety below)
        const hasConfigInterval = typeof configInterval === 'number' && !isNaN(configInterval) && configInterval! > 0;

        // 3) Determine a "nice" step aiming for ~4–8 ticks
        const targetTicks = 6; // mid of 4–8
        const computeNiceStep = (rawRange: number): number => {
            if (rawRange <= 0) return 1;
            const pow10 = Math.pow(10, Math.floor(Math.log10(rawRange / targetTicks)));
            const candidates = [1, 2, 2.5, 5].map(m => m * pow10);
            // Choose step that yields ticks closest to target
            let best = candidates[0];
            let bestDiff = Math.abs(rawRange / best - targetTicks);
            for (const c of candidates) {
                const diff = Math.abs(rawRange / c - targetTicks);
                if (diff < bestDiff) { best = c; bestDiff = diff; }
            }
            // For small magnitudes, allow one more refinement tier (e.g., 0.5, 0.2, 0.1)
            if (best >= 1 && rawRange / best < 3.5) {
                // Keep as is for larger scales
                return best;
            }
            return best;
        };

        let range = maxValue - minValue;
        let step = hasConfigInterval ? (configInterval as number) : computeNiceStep(range);

        // 4) Snap min and max ticks to step grid
        const floorTo = (v: number, s: number) => Math.floor(v / s) * s;
        const ceilTo = (v: number, s: number) => Math.ceil(v / s) * s;

        let axisMin = (!hasNegativeValues && minValue >= 0) ? 0 : floorTo(minValue, step);
        let axisMax = ceilTo(maxValue, step);

        // Always ensure a headroom above data max
        if (axisMax <= maxValue) axisMax += step;

        // Optional snap-to-integer rule: if step cleanly divides 1 (e.g., 0.5, 0.2, 0.1),
        // snap the top bound to the next integer for cleaner labels (e.g., 2.8 -> 3.0)
        if (step > 0) {
            const inv = 1 / step;
            const eps = 1e-6;
            if (Math.abs(inv - Math.round(inv)) < eps) {
                const snappedMax = Math.ceil(axisMax);
                // Only apply if tick count stays reasonable (<= 12)
                const tentativeTicks = Math.round((snappedMax - axisMin) / step);
                if (tentativeTicks >= 4 && tentativeTicks <= 12) {
                    axisMax = snappedMax;
                }
            }
        }

        // If an explicit configMax is provided, prefer it but still ensure it covers dataMax
        if (typeof configMax === 'number' && !isNaN(configMax)) {
            axisMax = Math.max(axisMax, configMax as number);
        }

        // If an explicit configMin is provided, prefer it but keep non-negative rule
        if (typeof configMin === 'number' && !isNaN(configMin)) {
            axisMin = !hasNegativeValues ? 0 : Math.min(axisMin, configMin as number);
        }

        // 5) Recompute step if tick count is out of bounds (fallback safety)
        const tickCount = Math.max(2, Math.round((axisMax - axisMin) / step));
        if (!hasConfigInterval && (tickCount < 4 || tickCount > 10)) {
            step = computeNiceStep(axisMax - axisMin);
            axisMin = (!hasNegativeValues && axisMin >= 0) ? 0 : floorTo(minValue, step);
            axisMax = ceilTo(maxValue, step);
            if (axisMax <= maxValue) axisMax += step;
        }

        // 6) Normalize decimals (avoid FP drift)
        const decimalPlaces = step < 1 ? Math.min(3, Math.max(1, -Math.floor(Math.log10(step)))) : 1;
        const roundTo = (v: number) => parseFloat(v.toFixed(decimalPlaces));

        return {
            min: roundTo(axisMin),
            max: roundTo(axisMax),
            interval: parseFloat(step.toFixed(decimalPlaces))
        };
    }
        /**
     * Get the data view table formatter function
     * @returns Function that formats chart data as HTML table
     */
    public static getDataViewTableFormatter(firstColumnHeader: string = 'Period'): (opt: any) => string {
        return function(opt: any) {
            console.log('DataView optionToContent called with:', opt);
            
            // Try to get source from dataset (can be array or object)
            let source: any[] | null = null;
            const dataset = opt.dataset;
            
            if (dataset) {
                if (Array.isArray(dataset) && dataset[0] && dataset[0].source) {
                    source = dataset[0].source;
                } else if (dataset.source) {
                    source = dataset.source;
                }
            }
            
            // Fallback: try to extract data from series
            if (!source || source.length === 0) {
                const series = opt.series;
                const xAxis = opt.xAxis;
                
                if (series && Array.isArray(series) && series.length > 0) {
                    // Build source from series data
                    source = [];
                    
                    // Get x-axis categories as header row
                    let categories: string[] = ['Period'];
                    if (xAxis && Array.isArray(xAxis) && xAxis[0] && xAxis[0].data) {
                        categories = ['Period', ...xAxis[0].data];
                    }
                    source.push(categories);
                    
                    // Add each series as a row
                    series.forEach((s: any) => {
                        if (s.data && Array.isArray(s.data)) {
                            const row = [s.name || 'Series', ...s.data];
                            source!.push(row);
                        }
                    });
                }
            }
            
            if (!source || source.length === 0) {
                console.log('No data found in opt:', opt);
                return '<div style="padding: 10px;">No data available</div>';
            }
            
            console.log('Building table from source:', source);
            
            // Build HTML table
            let table = '<table style="width:100%; border-collapse: collapse; font-size: 12px; text-align: center;">';
            
            // Header row with time periods
            table += '<thead><tr style="background-color: #f5f5f5;">';
            table += `<th style="border: 1px solid #ddd; padding: 8px; font-weight: 600;">${firstColumnHeader}</th>`;
            
            const headers = source[0];
            if (Array.isArray(headers)) {
                for (let i = 1; i < headers.length; i++) {
                    table += `<th style="border: 1px solid #ddd; padding: 8px; font-weight: 600;">${headers[i]}</th>`;
                }
            }
            table += '</tr></thead>';
            
            // Data rows (SSP scenarios)
            table += '<tbody>';
            for (let i = 1; i < source.length; i++) {
                const row = source[i];
                if (!Array.isArray(row)) continue;
                
                table += '<tr>';
                table += `<td style="border: 1px solid #ddd; padding: 8px; font-weight: 600; background-color: #f9f9f9;">${row[0]}</td>`;
                
                for (let j = 1; j < row.length; j++) {
                    const value = row[j];
                    let displayValue: string;
                    if (typeof value === 'number') {
                        displayValue = value.toFixed(3);
                    } else if (typeof value === 'string' && !isNaN(parseFloat(value))) {
                        displayValue = parseFloat(value).toFixed(3);
                    } else {
                        displayValue = value || '-';
                    }
                    table += `<td style="border: 1px solid #ddd; padding: 8px;">${displayValue}</td>`;
                }
                table += '</tr>';
            }
            table += '</tbody></table>';
            
            return table;
        };
    }

    /**
     * Format chart values for display (handles null, undefined, N/A, and numbers)
     * @param value - The value to format
     * @returns Formatted string value
     */
    public static formatChartValue(value: any): string {
        if (value === null || value === undefined || value === 'N/A' || value === '-') {
            return '-';
        }
        return typeof value === 'number' ? value.toFixed(3) : value;
    }

    /** Default xAxis config used across all climate charts */
    private static readonly DEFAULT_XAXIS_CONFIG = {
        type: 'category',
        boundaryGap: ['10%', '10%'],
        axisLabel: { interval: 0 }
    };

    /** Cached dual xAxis config to avoid recreation */
    private static _cachedDualXAxisConfig: any[] | null = null;

    /**
     * Pad scenario data to align with xAxis categories.
     * Pass xAxis when available so padding is driven by actual axis length:
     *   xAxis.length > 3  → historical column present → insert leading '-' for SSP rows
     *   xAxis.length <= 3 → no historical column      → return SSP row as-is
     * Omitting xAxis keeps the old behaviour (historical assumed present) for all
     * other chart services that have not been updated.
     */
    public static padSSPScenarioData(key: string, value: any[], xAxis?: string[]): any[] {
        const isHistorical = key.toLowerCase() === 'historical';
        // xAxis[0] is the dimension label ("Year Range"), so actual period columns = xAxis.length - 1.
        // 4 periods (label + 1995-2014 + 3 future) means historical is present → length > 4.
        // 3 periods (label + 3 future only) → length === 4, no historical column.
        const hasHistoricalColumn = xAxis ? xAxis.length > 4 : true;
        if (isHistorical && value.length === 2) {
            return [value[0], value[1], '-', '-', '-'];
        }
        if (!isHistorical && value.length === 4) {
            return hasHistoricalColumn
                ? [value[0], '-', value[1], value[2], value[3]]
                : value;
        }
        return value;
    }

    /**
     * Get series configuration with proper styling for historical vs SSP data
     * Historical uses xAxisIndex 1 for centering when useDualXAxis is true
     */
    public static getSeriesConfig(key: string, chartType: string, getColorFn: (key: string) => string, useDualXAxis: boolean = false): any {
        const isHistorical = (key || '').trim().toLowerCase() === 'historical';
        const config: any = {
            type: chartType,
            seriesLayoutBy: 'row',
            smooth: true,
            itemStyle: { color: getColorFn(key) }
        };
        if (useDualXAxis && isHistorical) {
            config.xAxisIndex = 1;
        }
        return config;
    }
    
    /**
     * Get dual xAxis configuration for centering Historical bar independently.
     * Uses cached config when called with default parameters.
     */
    public static getDualXAxisConfig(baseConfig?: any): any[] {
        const config = baseConfig || this.DEFAULT_XAXIS_CONFIG;
        // Return cached version for default config
        if (!baseConfig && this._cachedDualXAxisConfig) {
            return this._cachedDualXAxisConfig;
        }
        const result = [
            { ...config },
            { ...config, show: false, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { show: false } }
        ];
        if (!baseConfig) {
            this._cachedDualXAxisConfig = result;
        }
        return result;
    }

    /**
     * Get default chart options with dual xAxis pre-configured
     */
    public static getDefaultChartOptions(yAxisName: string, toolboxTitle: string, toolboxFileName: string): any {
        return {
            height: '80%',
            animation: false,
            animationDuration: 0,
            animationDurationUpdate: 0,
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross', label: { backgroundColor: '#6a7985' } },
                valueFormatter: (value: any) => this.formatChartValue(value)
            },
            legend: { top: 10 },
            toolbox: this.getToolboxConfig(toolboxTitle, toolboxFileName),
            dataZoom: { show: false, start: 0, end: 100 },
            grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
            xAxis: this.getDualXAxisConfig(),
            yAxis: [{
                type: 'value',
                scale: true,
                name: yAxisName,
                position: 'left',
                alignTicks: true,
                axisLine: { show: true },
                axisLabel: { formatter: '{value}' },
                boundaryGap: [0.2, 0.2]
            }],
            series: [],
            dataset: {}
        };
    }

    /**
     * Build series and source data from climate chart
     */
    public static buildChartData(climateChart: any, getColorFn: (key: string) => string): { seriesData: any[], source: any[] } {
        const seriesData: any[] = [];
        const source: any[] = [climateChart.xAxis];
        for (const [key, value] of Object.entries(climateChart.series)) {
            seriesData.push(this.getSeriesConfig(key, climateChart.chartConfig.type, getColorFn, true));
            source.push(this.padSSPScenarioData(key, value as any[], climateChart.xAxis));
        }
        return { seriesData, source };
    }

    /**
     * Attach finished event handler to maintain dual xAxis
     */
    public static attachDualXAxisHandler(echartsInstance: any): void {
        if (!echartsInstance?.on) return;
        let applying = false;
        try { echartsInstance.off?.('finished'); } catch {}
        echartsInstance.on('finished', () => {
            if (applying) return;
            applying = true;
            echartsInstance.setOption({ xAxis: this.getDualXAxisConfig() }, { replaceMerge: ['xAxis'] });
            setTimeout(() => { applying = false; }, 0);
        });
    }

}